import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ClipboardList } from 'lucide-react';
import { Page, PageHeader } from '@/components/page';
import { RelativeTime } from '@/components/relative-time';
import { GoalBadges, reportPeriod, ReportStateBadge } from '@/components/report/report-parts';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useDesks } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

const ALL = '__all';

/** Every desk's reports, newest first (docs/features/desk-reports.md). */
export function ReportsPage() {
  const { workspaceId } = useParams({ from: '/_authed/w/$workspaceId/reports' });
  const { deskId } = useSearch({ from: '/_authed/w/$workspaceId/reports' });
  const navigate = useNavigate({ from: '/w/$workspaceId/reports' });
  const desks = useDesks(workspaceId);
  const reports = useQuery({
    queryKey: qk.reports(workspaceId, deskId),
    queryFn: () => api.reports.list(workspaceId, { deskId }),
    placeholderData: keepPreviousData,
  });
  return (
    <Page>
      <PageHeader
        title={t('Reports')}
        description={t("Each desk's Lead reports where it stands on its goals. Answer in a report's thread; when everything is said, the Lead improves its desk.")}
        actions={
          <Select value={deskId ?? ALL} onValueChange={(v) => void navigate({ search: { deskId: v === ALL ? undefined : v }, replace: true })}>
            <SelectTrigger size="sm" className="w-56">
              <SelectValue placeholder={t('Desk')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('All desks')}</SelectItem>
              {(desks.data ?? []).map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      {reports.isPending ? (
        <LoadingRows rows={4} />
      ) : reports.isError ? (
        <ErrorState error={reports.error} onRetry={() => void reports.refetch()} />
      ) : reports.data.length === 0 ? (
        <EmptyState
          icon={<ClipboardList />}
          title={t('No reports yet')}
          description={t('Set how often a desk reports on its page (The Lead → Reports), or click “Report now” there.')}
        />
      ) : (
        <Card className="divide-y overflow-hidden p-0">
          {reports.data.map((r) => (
            <Link
              key={r.id}
              to="/w/$workspaceId/work-items/$workItemId"
              params={{ workspaceId, workItemId: r.workItemId }}
              className="flex flex-col gap-1 px-4 py-3 hover:bg-muted/40"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">{r.deskName}</span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{r.title}</span>
                <GoalBadges report={r} />
                <ReportStateBadge report={r} />
              </div>
              <div className="flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                <span>{reportPeriod(r)}</span>
                {r.quiet && <span>{t('quiet period')}</span>}
                {r.summary && <span className="min-w-0 truncate">{r.summary}</span>}
                <span className="ml-auto">
                  <RelativeTime value={r.createdAt} />
                </span>
              </div>
            </Link>
          ))}
        </Card>
      )}
    </Page>
  );
}
